/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Modal from '../../../../commonComponents/modal/Modal';
import { useModal } from '../../../../services/customHooks';
import BillingStep from './steps/BillingStep';
import ShopPriceEstimation from '../../../shop/shopItems/ShopPriceEstimation';
import PaymentStep from './steps/PaymentStep';
import OrderStep from './steps/OrderStep';
import ReactLoader from '../../../../commonComponents/loader/ReactLoader';
import OrderWizard from '../../../../commonComponents/modal/components/OrderWizard';
import Notifications from '../../../../services/notifications';
import { paymentReducer } from '../../../../commonComponents/modal/AcademyPaymentModal';
import { getCreditCardList } from '../../../../services/checkoutHelper';
import TenantProp from '../../../../commonComponents/propTypes/tenantProp';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import CheckoutTerms from '../../../shop/components/CheckoutTerms';
import { STEP_PLAN_CHECKOUT } from '../../../plan/propTypes/planProp';
import { handleStripePayment } from '../../../../services/utilityFunctions';

const initialStatePayment = {
  accountHolderName: '',
  selectedCreditCard: null,
  userCreditCards: [],
};

function SubscriptionPlanModal({
  t,
  tenant,
  _csrf,
  countries,
  setHasPlans,
  onPlansModalClose,
  forceReload,
  showClose,
  onClose,
}) {
  const [{ show: isModalShown }, setShowModal] = useModal({ show: true });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlanOrderDetails, setSelectedPlanOrderDetails] = useState();
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [billingAddress, setBillingAddress] = useState({});
  const [selectedPlan, setSelectedPlan] = useState();
  const [step, setStep] = useState(STEP_PLAN_CHECKOUT.ORDER);
  const [plans, setPlans] = useState([]);
  // Terms of conditions
  const [checkedTerms, setCheckedTerms] = useState(false);

  const toggleModal = useCallback(() => {
    setShowModal({ show: !isModalShown });
    onPlansModalClose();
  }, [isModalShown]);
  const handleIsLoading = loading => setIsPaymentProcessing(loading);

  const { paymentMethods = [] } = tenant.settings;
  const [planPayment, dispatchPlanPayment] = useReducer(
    paymentReducer,
    initialStatePayment,
    () => ({
      ...initialStatePayment,
      tabActive: paymentMethods[0],
      companyBilling: {
        name: '',
        phone: '',
        taxCode: '',
        taxType: '',
        vatNumber: '',
        address: {
          country: '',
          address_1: '',
          address_2: '',
          city: '',
          zipCode: '',
        },
      },
    }),
  );

  const handleCheckbox = () => {
    setCheckedTerms(!checkedTerms);
  };
  const updatePaymentMethod = (type, payload) => {
    dispatchPlanPayment({ type, payload });
  };

  useEffect(() => {
    try {
      const getCardsList = async () => {
        const cardsList = await getCreditCardList(_csrf, true, true);
        const defaultCard =
          cardsList && cardsList.length > 0
            ? cardsList.find(c => c.isDefault)?.id || null
            : null;
        dispatchPlanPayment({
          type: 'updatedUserCards',
          payload: { cards: cardsList, cardId: defaultCard },
        });
      };
      getCardsList();
    } catch (err) {
      Notifications.showErrorFromRequest(err, t('error_fetchCards'));
    }
  }, [dispatchPlanPayment, _csrf]);

  const { tabActive, selectedCreditCard, userCreditCards } = planPayment;

  useEffect(() => {
    setPaymentMethodId(selectedCreditCard);
  }, [selectedCreditCard]);

  useEffect(() => {
    const params = {
      currentUserPlan: selectedPlan?._id,
    };
    if (params?.currentUserPlan) {
      if (billingAddress) {
        params.billingAddress = billingAddress;
      }
      axios
        .post('/api/subscription/subscribe/calculate', params, {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'License',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        })
        .then(({ data: response }) => {
          setSelectedPlanOrderDetails(response.data?.currentUserPlan);
        })
        .catch(err =>
          Notifications.showNotificationError(err, t('error.subscribing')),
        );
    }
  }, [selectedPlan?._id, billingAddress]);

  // Get subscriptions
  useEffect(() => {
    (async () => {
      const { data } = await axios.get('/api/plan/user/list', {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'License',
        },
      });

      if (data.success) {
        setPlans(data.data);

        setHasPlans(!!data.data.length);

        if (data.data.length) {
          setSelectedPlan(data.data[0]);
        }
      }
      setIsLoading(false);
    })();
  }, []);

  const confirmPayment = useCallback(async () => {
    try {
      setIsPaymentProcessing(true);

      const { data: response } = await axios.post(
        '/api/subscription/subscribe',
        {
          currentUserPlan: selectedPlan._id,
          billingAddress,
          paymentMethodId,
        },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'License',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );
      const subscription = response.data;
      const { status: paymentIntentStatus, client_secret } =
        subscription?.paymentIntent || {};

      if (
        paymentIntentStatus === 'requires_action' ||
        paymentIntentStatus === 'requires_confirmation' ||
        paymentIntentStatus === 'requires_payment_method'
      ) {
        await handleStripePayment(paymentMethodId, client_secret);
      }

      if (forceReload) {
        window.location.reload();
      } else {
        toggleModal();
        onPlansModalClose(selectedPlan);
      }
    } catch (err) {
      Notifications.showErrorFromRequest(err, t('error.subscribing.subscribe'));
      toggleModal();
    } finally {
      setIsPaymentProcessing(false);
    }
  }, [selectedPlan, setBillingAddress, billingAddress, paymentMethodId]);

  const goToNextStep = useCallback(() => {
    if (step === STEP_PLAN_CHECKOUT.ORDER) {
      setStep(STEP_PLAN_CHECKOUT.BILLING);
    } else {
      setStep(STEP_PLAN_CHECKOUT.PAYMENT);
    }
  }, [step]);

  const goToPreviousStep = useCallback(() => {
    if (step === STEP_PLAN_CHECKOUT.PAYMENT) {
      setStep(STEP_PLAN_CHECKOUT.BILLING);
    } else {
      setStep(STEP_PLAN_CHECKOUT.ORDER);
    }
  }, [step]);

  const requiredInformation =
    billingAddress?.address_1 &&
    (selectedPlanOrderDetails?.userPrice?.total > 0 ? paymentMethodId : true);
  return (
    isModalShown && (
      <Modal id="subscriptionPlanModal" escToClose={false}>
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          role="document"
        >
          <div className="modal-content">
            <div className="subscription-plan-modal-popup">
              <div className="subscription-plan-modal-popup-wrapper">
                <div className="subscription-plan-modal-popup-content">
                  {step === STEP_PLAN_CHECKOUT.ORDER && (
                    <ReactLoader
                      isLoading={isLoading}
                      style={!isLoading ? undefined : {}}
                    >
                      <OrderStep
                        t={t}
                        plans={plans}
                        selectedPlan={selectedPlan}
                        setSelectedPlan={setSelectedPlan}
                        step={step}
                      />
                    </ReactLoader>
                  )}
                  {step === STEP_PLAN_CHECKOUT.BILLING && (
                    <div className="subscription-plan-billing-step">
                      <BillingStep
                        t={t}
                        _csrf={_csrf}
                        tenant={tenant}
                        countries={countries}
                        billingAddress={billingAddress}
                        setBillingAddress={setBillingAddress}
                        setPaymentMethodId={setPaymentMethodId}
                        setSelectedPlanOrderDetails={
                          setSelectedPlanOrderDetails
                        }
                        selectedPlanOrderDetails={selectedPlanOrderDetails}
                        step={step}
                      />
                    </div>
                  )}
                  {step === STEP_PLAN_CHECKOUT.PAYMENT && (
                    <div className="subscription-plan-payment-step">
                      <div className="details">
                        <div className="order-wizard-container">
                          <OrderWizard
                            activeStep={
                              step === 'order' ? 1 : step === 'billing' ? 2 : 3
                            }
                          />
                          <div className="billing-container payment-container">
                            <PaymentStep
                              _csrf={_csrf}
                              tenant={tenant}
                              setPaymentMethodId={setPaymentMethodId}
                              handleIsLoading={handleIsLoading}
                              userCreditCards={userCreditCards}
                              tabActive={tabActive}
                              updatePaymentMethod={updatePaymentMethod}
                              selectedCreditCard={selectedCreditCard}
                              showSaveCard={false}
                              currency={selectedPlan?.userPrice?.currency}
                              hidePaypal
                            />
                          </div>
                        </div>
                        <div className="sideBar-wrapper">
                          <div className="subscription-plan-modal-popup-price-info">
                            <ShopPriceEstimation
                              frequency={t(
                                `chartPortlet.${selectedPlanOrderDetails?.interval}`,
                              )}
                              orderDetails={{
                                pricesWithoutVAT:
                                  selectedPlanOrderDetails?.userPrice
                                    .priceWithoutVAT,
                                total:
                                  selectedPlanOrderDetails?.userPrice?.total ??
                                  0,
                                subtotal:
                                  selectedPlanOrderDetails?.userPrice?.price ??
                                  0,
                                symbol:
                                  selectedPlanOrderDetails?.userPrice.symbol,
                                tax:
                                  selectedPlanOrderDetails?.userPrice?.vat ||
                                  null,
                              }}
                            />
                          </div>
                          <CheckoutTerms
                            onChange={handleCheckbox}
                            checked={checkedTerms}
                            tenant={tenant}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="subscription-plan-modal-popup-buttons-wrapper">
                    {plans.length > 0 && (
                      <>
                        {(step === STEP_PLAN_CHECKOUT.BILLING ||
                          step === STEP_PLAN_CHECKOUT.PAYMENT) && (
                          <button
                            className="secondary"
                            onClick={goToPreviousStep}
                            type="button"
                          >
                            {t('back')}
                          </button>
                        )}
                        {(step === STEP_PLAN_CHECKOUT.ORDER ||
                          step === STEP_PLAN_CHECKOUT.BILLING) && (
                          <button
                            type="button"
                            className="btn btn-dynamic primary"
                            onClick={goToNextStep}
                            disabled={
                              step === STEP_PLAN_CHECKOUT.BILLING &&
                              !billingAddress?.address_1
                            }
                          >
                            {t('next')}
                          </button>
                        )}
                        {step === STEP_PLAN_CHECKOUT.PAYMENT && (
                          <button
                            type="button"
                            className={`btn btn-dynamic primary ${
                              isPaymentProcessing
                                ? 'm-loader m-loader--right m-loader--light'
                                : ''
                            }`}
                            disabled={
                              !checkedTerms ||
                              isPaymentProcessing ||
                              !requiredInformation
                            }
                            onClick={confirmPayment}
                          >
                            {t('checkout.complete.order')}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {showClose && (
                  <button type="button" onClick={onClose} className="btn-close">
                    <span>x</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    )
  );
}

SubscriptionPlanModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  setHasPlans: PropTypes.func.isRequired,
  onPlansModalClose: PropTypes.func.isRequired,
  forceReload: PropTypes.bool,
  showClose: PropTypes.bool,
  onClose: PropTypes.func,
};

SubscriptionPlanModal.defaultProps = {
  forceReload: false,
  showClose: false,
  onClose: () => {},
};

export default SubscriptionPlanModal;
